import { Box } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { COVERAGE, GRID_FONT, INK } from "../../../../theme/tokens";
import { EXPANDED_ROW_MIN_H, leadingOffsets } from "../gridGeometry";
import { coverageRowLabel, formatRotationRule, type CoverageRow as RowType } from "../types";
import { CoverageIdentityLabel } from "./CoverageIdentityLabel";
import { StickyLeadingCell } from "./StickyLeadingCell";

// Detail uses the COMPACT leading widths — the identity zone shrinks so the freed
// width goes to the name chips, which are what matters here.
const { shift: SHIFT_LEFT, role: ROLE_LEFT } = leadingOffsets("compact");

/**
 * The three compact sticky leading cells of a Coverage · Detail row. Detail
 * prioritizes the NAME over the slot, so these columns are tight + small-type;
 * the recurrence rule + outside-config flag move into a Location tooltip rather
 * than eating chip width inline. Split out of `DetailRow` and memoized.
 */
export const DetailRowIdentity = memo(({ row }: { row: RowType }) => {
	const { t } = useTranslation();
	// Job-managed depts render the job (localized "Unassigned" for the job-less sentinel); non-job
	// depts fall back to the role-set label — resolved in one shared helper.
	const identityLabel = coverageRowLabel(row, t);
	const rowLabel = `${row.location} · ${identityLabel} · ${row.shift}`;
	const ruleLabel = formatRotationRule(row.rule, t);
	return (
		<>
			{/* Detail truncates the Location label hard — always surface the full
			    location name on hover, appending the recurrence rule when present. */}
			<InHouseTooltip title={ruleLabel ? `${row.location} · ${t("schedule.template.v3.ruleTooltip", { rule: ruleLabel })}` : rowLabel}>
				<StickyLeadingCell left={0} variant="body" role="rowheader" emphasis="primary" minHeight={EXPANDED_ROW_MIN_H} gap={0.5} sx={{ fontSize: GRID_FONT.SM }}>
					<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{row.location}
					</Box>
					{row.outsideConfig && (
						<Box component="span" sx={{ color: COVERAGE.WARNING_INK, fontWeight: 800, ml: 0.25, flexShrink: 0 }}>
							*
						</Box>
					)}
				</StickyLeadingCell>
			</InHouseTooltip>
			<StickyLeadingCell
				left={SHIFT_LEFT}
				variant="body"
				role="rowheader"
				emphasis="secondary"
				minHeight={EXPANDED_ROW_MIN_H}
				gap={0.5}
				sx={{ fontSize: GRID_FONT.SM }}
			>
				<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{row.shift}
				</Box>
			</StickyLeadingCell>
			<StickyLeadingCell
				left={ROLE_LEFT}
				variant="body"
				role="rowheader"
				emphasis="secondary"
				showEdgeShadow
				minHeight={EXPANDED_ROW_MIN_H}
				gap={0.5}
				sx={{ fontSize: GRID_FONT.XS }}
			>
				<CoverageIdentityLabel
					label={identityLabel}
					unconfiguredRoles={row.unconfiguredRoles}
					baseColor={INK.SECONDARY}
					sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
				/>
			</StickyLeadingCell>
		</>
	);
});
